import { Box, BoxProps, Button, Card, Text, VStack } from "@chakra-ui/react";
import { useFieldArray, useFormContext } from "react-hook-form";

import PointerPressIcon from "@/common/assets/pointer-press.svg?react";
import { AndOrList } from "@/common/components/AndOrList";
import { DeleteIconButton } from "@/common/components/CommonIconButtons";
import { CopyInput } from "@/common/components/CopyInput";
import { FormEventAttributeFilterPickerInput } from "@/feature/components/Form/FormEventAttributeFilterPicker";
import { FormEventAutoCompleteInputOnly } from "@/feature/components/Form/FormEventAutoCompleteInput";
import { EventSelectorType } from "@reflag/shared/featureAPI";

function EventBlock({ children, ...props }: BoxProps) {
  return (
    <Card key="feature-key" {...props} p={4} variant="filled" w="full">
      {children}
    </Card>
  );
}

export function FormEventBasedFilterInputs({
  name,
  w,
  featureKey,
}: {
  name: string;
  w?: string;
  featureKey?: string;
}) {
  const form = useFormContext();
  const formSelectors = useFieldArray({
    control: form.control,
    name: name,
  });

  return (
    <VStack align="flex-start" w={w}>
      <AndOrList
        conjunction="or"
        conjunctionProps={{ ml: 4, my: -1, zIndex: 1 }}
        direction="vertical"
        gap="0"
        w="full"
      >
        {featureKey && (
          <EventBlock pr={4}>
            <CopyInput
              _input={{ bg: "appBackground" }}
              flexGrow={1}
              leftIcon={<PointerPressIcon height="16px" width="16px" />}
              value={featureKey}
              variant="outline"
            />
            <Text color="dimmed" fontSize="sm" mt={2}>
              Use the flag key for tracking feature adoption
            </Text>
          </EventBlock>
        )}
        {formSelectors.fields.map((field, index) => (
          <EventBlock key={field.id}>
            <FormEventSelectorInput
              key={field.id}
              nameBase={`${name}.${index}`}
            />
            <DeleteIconButton
              label={formSelectors.fields.length < 2 ? "" : "Remove"}
              position="absolute"
              right={4}
              top={4}
              onClick={() => formSelectors.remove(index)}
            />
          </EventBlock>
        ))}
      </AndOrList>

      <Button
        isDisabled={form.formState.isSubmitting}
        size="sm"
        variant="input"
        onClick={() => formSelectors.append({ name: "", filter: [] })}
      >
        Add custom event
      </Button>
    </VStack>
  );
}

function FormEventSelectorInput({ nameBase }: { nameBase: string }) {
  const form = useFormContext();
  const field: EventSelectorType = form.watch(nameBase);

  return (
    <VStack align="flex-start" spacing={2}>
      <Box pr="40px" w="full">
        <FormEventAutoCompleteInputOnly
          name={`${nameBase}.name`}
          placeholder="Your event name"
        />
      </Box>

      <FormEventAttributeFilterPickerInput
        buttonText="Add attribute filter"
        eventName={field.name}
        name={`${nameBase}.filter`}
        size="sm"
      />
    </VStack>
  );
}
